import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action";
import axios from "../../../../shared/axios/axiosProxy";
import i18n from "../../../../utils/i18n";
import classes from "./evCegjelzoTaxForm.module.scss";

const ATALANY_SUB_OPTIONS = [
  {
    value: "singlesecondary",
    title: i18n.t("searchflow.secondaryemployment"),
    subtitle: i18n.t("searchflow.secondaryemploymentsubtitle"),
  },
  {
    value: "kiva",
    title: i18n.t("searchflow.primaryemployment"),
    subtitle: i18n.t("searchflow.primaryemploymentsubtitle"),
  },
];

const STANDALONE_OPTIONS = [
  { value: "singleprimary", title: i18n.t("providervafform.singleprimary") },
  { value: "vszja", title: i18n.t("searchflow.vszja") },
];

const Radio = ({ checked }) => (
  <span
    className={`${classes.radio} ${checked ? classes.radioChecked : ""}`}
    aria-hidden="true"
  />
);

const EvCegjelzoTaxForm = ({ onInteractedChange, initialInteracted = false } = {}) => {
  const companyStore = useSelector((state) => state.company_store);
  const dispatch = useDispatch();
  const companytype = companyStore.companytype;
  const isAtalanySubValue =
    companytype === "kiva" || companytype === "singlesecondary";

  // The Átalányadó group starts collapsed and no option starts selected, even if a
  // default companytype is already stored (e.g. the Cégjelző self-employed default) —
  // only the user's own click on this screen reveals/selects anything (and enables
  // Next; see searchFlowFooterNav.js). initialInteracted carries a real earlier pick
  // forward across step revisits (reset upstream whenever the company type selection
  // changes — see searchFlow.jsx — so a stale default can never masquerade as a real pick).
  const [atalanyExpanded, setAtalanyExpanded] = useState(
    initialInteracted && isAtalanySubValue,
  );
  const [hasInteracted, setHasInteracted] = useState(initialInteracted);

  useEffect(() => {
    onInteractedChange?.(hasInteracted);
  }, [hasInteracted, onInteractedChange]);

  // Opening the group itself marks its own radio as selected right away — picking
  // between Mellékállásban/Főállásban underneath is a separate, still-required step
  // that only "hasInteracted" (set by an actual sub/standalone pick) unlocks.
  const isAtalanyGroupSelected = atalanyExpanded;

  const syncSearchTaxFields = useCallback((searchID, nextCompanytype, taxform) => {
    if (!searchID) {
      return;
    }
    axios
      .patch(
        `/api/search/${searchID}`,
        { companytype: nextCompanytype, taxform },
        { headers: { "Content-Type": "application/json" } },
      )
      .catch((error) => {
        console.error(
          "Error saving taxform to searchSchema:",
          error.response?.data || error.message,
        );
      });
  }, []);

  const updateCompanyType = useCallback(
    (nextCompanytype) => {
      const taxform = "";

      setHasInteracted(true);
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "companytype",
          data: nextCompanytype,
          stateObject: "company_store",
        }),
      );
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "taxform",
          data: taxform,
          stateObject: "company_store",
        }),
      );
      syncSearchTaxFields(companyStore.searchID, nextCompanytype, taxform);
    },
    [companyStore.searchID, dispatch, syncSearchTaxFields],
  );

  const clearCompanyTypeSelection = useCallback(() => {
    setHasInteracted(true);
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "companytype",
        data: "",
        stateObject: "company_store",
      }),
    );
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "taxform",
        data: "",
        stateObject: "company_store",
      }),
    );
    syncSearchTaxFields(companyStore.searchID, "", "");
  }, [companyStore.searchID, dispatch, syncSearchTaxFields]);

  const handleAtalanyHeaderClick = () => {
    setAtalanyExpanded(true);
    if (companytype === "singleprimary" || companytype === "vszja") {
      clearCompanyTypeSelection();
    }
  };

  const handleAtalanySubSelect = (value) => {
    setAtalanyExpanded(true);
    updateCompanyType(value);
  };

  const handleStandaloneSelect = (value) => {
    setAtalanyExpanded(false);
    updateCompanyType(value);
  };

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>{i18n.t("searchflow.evtaxformtitle")}</h1>
              <p className={classes.subtitle}>
                {i18n.t("searchflow.evtaxformsubtitle")}
              </p>
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.cardsContainer}>
                <div
                  className={`${classes.card} ${classes.cardExpandable} ${atalanyExpanded ? classes.cardExpandableExpanded : ""} ${isAtalanyGroupSelected ? classes.selectedRow : ""}`}
                >
                  <button
                    type="button"
                    className={classes.cardHeader}
                    onClick={handleAtalanyHeaderClick}
                    aria-expanded={atalanyExpanded}
                    aria-pressed={isAtalanyGroupSelected}
                  >
                    <div className={classes.cardContent}>
                      <div className={classes.cardTitle}>{i18n.t("companylabels.evatalany")}</div>
                    </div>
                    <Radio checked={isAtalanyGroupSelected} />
                  </button>

                  <div className={classes.subOptions} aria-hidden={!atalanyExpanded}>
                    {ATALANY_SUB_OPTIONS.map((option) => {
                      const isSelected = hasInteracted && companytype === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`${classes.subOption} ${isSelected ? classes.subOptionSelected : ""}`}
                          onClick={() => handleAtalanySubSelect(option.value)}
                          aria-pressed={isSelected}
                          tabIndex={atalanyExpanded ? 0 : -1}
                        >
                          <div className={classes.subOptionContent}>
                            <div className={classes.subOptionTitle}>{option.title}</div>
                            <div className={classes.subOptionSubtitle}>{option.subtitle}</div>
                          </div>
                          <Radio checked={isSelected} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {STANDALONE_OPTIONS.map((option) => {
                  const isSelected = hasInteracted && companytype === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`${classes.card} ${isSelected ? classes.selectedRow : ""}`}
                      onClick={() => handleStandaloneSelect(option.value)}
                      aria-pressed={isSelected}
                    >
                      <div className={classes.cardContent}>
                        <div className={classes.cardTitle}>{option.title}</div>
                      </div>
                      <Radio checked={isSelected} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvCegjelzoTaxForm;
